import dummyData from './dummyData.json';
import loginMenuJson from './loginMenu.json';

export interface INavigationOutlet {
  outlet: string;
  menu: INavigationItem[];
  rootPath: string;
}

export interface INavigationItem {
  title: string;
  path: string;
  children?: INavigationItem[];
  active?: boolean;
  external?: boolean;
  marked?: boolean;
}

export enum EOutletTypes {
  header = 'mainMenu',
  footer = 'footerMenu',
  loginMenu = 'loginMenu',
}

export type publicApi = {
  consent: () => Promise<unknown>;
  getFeatures: () => Promise<unknown>;
  getNavigationOutlets: () => Promise<INavigationOutlet[]>;
  getNextPageConfiguration: () => Promise<unknown>;
  getPageConfiguration: () => Promise<unknown>;
};

export abstract class Navigation {
  constructor(protected outlet: INavigationOutlet) {}

  public get rootPath(): string {
    return this.outlet.rootPath;
  }

  public get topLevel(): INavigationItem[] {
    return this.outlet.menu;
  }

  public getNavigationTree(path: string = window.location.pathname): INavigationItem[] {
    this.markActive(this.outlet.menu, path);
    const node = this.findNodeByPath(this.outlet.menu, path);
    return node?.children ?? [];
  }

  protected findNodeByPath(tree: INavigationItem[], path: string): INavigationItem | null {
    for (const node of tree) {
      if (node.path === path) return node;
      if (node.children?.length) {
        const found = this.findNodeByPath(node.children, path);
        if (found) return found;
      }
    }
    return null;
  }

  protected markActive(tree: INavigationItem[], path: string): boolean {
    let foundMatch = false;
    for (const item of tree) {
      const childMatch = item.children?.length ? this.markActive(item.children, path) : false;
      const isExactMatch = item.path === path;

      item.active = isExactMatch || childMatch;

      if (item.active) foundMatch = true;
    }
    return foundMatch;
  }
}

export class HeaderNavigation extends Navigation {
  public override get rootPath(): string {
    return window.location.origin;
  }

  public getSubNavigation(): INavigationItem[] {
    const path = window.location.pathname !== '/' ? window.location.pathname : this.rootPath;
    this.markActive(this.outlet.menu, path);

    return this.outlet.menu;
  }

  public override getNavigationTree(): INavigationItem[] {
    const path = window.location.pathname !== '/' ? window.location.pathname : this.rootPath;
    this.markActive(this.outlet.menu, path);
    return this.outlet.menu;
  }
}

export class FooterNavigation extends Navigation {}

export class LoginNavigation extends Navigation {
  public override getNavigationTree(): INavigationItem[] {
    return this.outlet.menu;
  }
}

class NavigationService {
  public headerMenu: HeaderNavigation | null = null;
  public footerMenu: FooterNavigation | null = null;
  public loginMenu: LoginNavigation | null = null;

  private initPromiseRef: Promise<void>;
  private useDummyData =
    window.location.host.includes('f0810-vp1-m0.ka.serviceprobe') &&
    window.location.search.includes('dummy=true');

  constructor() {
    this.initPromiseRef = this.initService();
  }

  private async initService() {
    let data = (await (
      (window as unknown as Window & { publicApi: publicApi }).publicApi as publicApi
    ).getNavigationOutlets()) as INavigationOutlet[];

    // can be set for debugging
    if (this.useDummyData) {
      data = dummyData;
    }

    const headerData = data.find(d => d.outlet === EOutletTypes.header);
    const footerData = data.find(d => d.outlet === EOutletTypes.footer);
    const loginData = loginMenuJson;

    if (!this.headerMenu && headerData) {
      this.headerMenu = new HeaderNavigation(headerData);
    }

    if (!this.footerMenu && footerData) {
      this.footerMenu = new FooterNavigation(footerData);
    }

    if (!this.loginMenu && loginData) {
      this.loginMenu = new LoginNavigation(loginData);
    }
  }

  public async whenReady() {
    return this.initPromiseRef;
  }

  public refresh() {
    this.initService();
  }
}

const navigationServiceInstance = new NavigationService();
export { navigationServiceInstance as NavigationService };
